import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Loader2, Search, Radio, ExternalLink, Trash2, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface ChatterRow {
  id: string;
  source_url: string;
  source_domain: string | null;
  title: string | null;
  snippet: string | null;
  search_query: string;
  ai_score: number | null;
  ai_reasoning: string | null;
  ai_seller_signals: string[] | null;
  extracted_email: string | null;
  extracted_phone: string | null;
  status: string;
  notes: string | null;
  created_at: string;
}

const PRESET_QUERIES = [
  '"sell my land contract"',
  '"sell my mortgage note"',
  '"cash out my note" owner financing',
  '"selling my promissory note"',
  '"need to sell" "land contract"',
  'site:reddit.com "sell" "land contract"',
  'site:biggerpockets.com selling note for cash',
  'site:craigslist.org "land contract" "for sale by owner"',
  '"holding a note" want to sell',
  '"balloon payment" "selling" note',
];

const STATUS_COLORS: Record<string, string> = {
  new: "bg-blue-500",
  reviewed: "bg-amber-500",
  contacted: "bg-purple-500",
  converted: "bg-green-600",
  dismissed: "bg-gray-400",
};

export default function NoteSellerChatterPage() {
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState(15);
  const [tbs, setTbs] = useState<string>("");
  const [searching, setSearching] = useState(false);
  const [rows, setRows] = useState<ChatterRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [minScore, setMinScore] = useState<number>(0);

  const fetchRows = async () => {
    setLoading(true);
    let q = supabase
      .from("note_seller_chatter")
      .select("*")
      .order("ai_score", { ascending: false, nullsFirst: false })
      .order("created_at", { ascending: false })
      .limit(500);

    if (statusFilter !== "all") q = q.eq("status", statusFilter);
    if (minScore > 0) q = q.gte("ai_score", minScore);

    const { data, error } = await q;
    if (error) {
      toast.error(error.message);
    } else {
      setRows((data || []) as ChatterRow[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, minScore]);

  const runSearch = async () => {
    if (!query.trim() || query.trim().length < 3) {
      toast.error("Enter a search query (min 3 chars)");
      return;
    }
    setSearching(true);
    try {
      const { data, error } = await supabase.functions.invoke("scrape-note-seller-chatter", {
        body: { query: query.trim(), limit, tbs: tbs || undefined },
      });
      if (error) throw error;
      if (data?.success) {
        toast.success(data.message || "Search complete");
        await fetchRows();
      } else {
        toast.error(data?.error || "Search failed");
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Search failed");
    } finally {
      setSearching(false);
    }
  };

  const updateRow = async (id: string, patch: Partial<ChatterRow>) => {
    const { error } = await supabase.from("note_seller_chatter").update(patch).eq("id", id);
    if (error) {
      toast.error(error.message);
      return;
    }
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const deleteRow = async (id: string) => {
    const { error } = await supabase.from("note_seller_chatter").delete().eq("id", id);
    if (error) {
      toast.error(error.message);
      return;
    }
    setRows((prev) => prev.filter((r) => r.id !== id));
    toast.success("Deleted");
  };

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Radio className="h-7 w-7 text-primary" />
          Note Seller Chatter
        </h1>
        <p className="text-muted-foreground mt-1">
          Scan the web for people talking about selling their notes or land contracts. AI scores each hit 1–10 for seller intent.
        </p>
      </div>

      <Card className="p-6 space-y-4">
        <div className="space-y-2">
          <Label>Search Query</Label>
          <div className="flex gap-2">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder='e.g. "sell my land contract" Michigan'
              onKeyDown={(e) => {
                if (e.key === "Enter" && !searching) runSearch();
              }}
            />
            <Button onClick={runSearch} disabled={searching}>
              {searching ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Scanning...</>
              ) : (
                <><Search className="mr-2 h-4 w-4" />Scan Web</>
              )}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Results to fetch</Label>
            <Select value={String(limit)} onValueChange={(v) => setLimit(Number(v))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="15">15</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="30">30 (max)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Time filter</Label>
            <Select value={tbs || "any"} onValueChange={(v) => setTbs(v === "any" ? "" : v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any time</SelectItem>
                <SelectItem value="qdr:d">Past 24 hours</SelectItem>
                <SelectItem value="qdr:w">Past week</SelectItem>
                <SelectItem value="qdr:m">Past month</SelectItem>
                <SelectItem value="qdr:y">Past year</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-xs">Quick presets (click to use)</Label>
          <div className="flex flex-wrap gap-2">
            {PRESET_QUERIES.map((p) => (
              <Button
                key={p}
                variant="outline"
                size="sm"
                onClick={() => setQuery(p)}
                className="text-xs h-7"
              >
                {p}
              </Button>
            ))}
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex flex-wrap items-end gap-4 mb-4">
          <div className="space-y-1">
            <Label className="text-xs">Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="reviewed">Reviewed</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="converted">Converted</SelectItem>
                <SelectItem value="dismissed">Dismissed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Min score</Label>
            <Select value={String(minScore)} onValueChange={(v) => setMinScore(Number(v))}>
              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Any</SelectItem>
                <SelectItem value="4">4+</SelectItem>
                <SelectItem value="6">6+</SelectItem>
                <SelectItem value="8">8+ (best)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" size="sm" onClick={fetchRows} disabled={loading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <div className="ml-auto text-sm text-muted-foreground">
            {rows.length} result{rows.length === 1 ? "" : "s"}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            No chatter yet. Run a search above to get started.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Score</TableHead>
                  <TableHead>Result</TableHead>
                  <TableHead className="w-32">Source</TableHead>
                  <TableHead className="w-40">Status</TableHead>
                  <TableHead className="w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      {r.ai_score != null ? (
                        <Badge
                          className={`${
                            r.ai_score >= 8 ? "bg-green-600" :
                            r.ai_score >= 6 ? "bg-amber-500" :
                            r.ai_score >= 4 ? "bg-blue-500" : "bg-gray-400"
                          } text-white`}
                        >
                          {r.ai_score}
                        </Badge>
                      ) : (
                        <Badge variant="outline">—</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1 max-w-xl">
                        <a
                          href={r.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-semibold text-primary hover:underline flex items-start gap-1"
                        >
                          {r.title || r.source_url}
                          <ExternalLink className="h-3 w-3 mt-1 flex-shrink-0" />
                        </a>
                        {r.snippet && (
                          <p className="text-xs text-muted-foreground line-clamp-2">{r.snippet}</p>
                        )}
                        {r.ai_reasoning && (
                          <p className="text-xs italic text-muted-foreground">
                            <span className="font-semibold">AI:</span> {r.ai_reasoning}
                          </p>
                        )}
                        {r.ai_seller_signals && r.ai_seller_signals.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {r.ai_seller_signals.map((s, i) => (
                              <Badge key={i} variant="secondary" className="text-xs">{s}</Badge>
                            ))}
                          </div>
                        )}
                        {(r.extracted_email || r.extracted_phone) && (
                          <div className="text-xs mt-1">
                            {r.extracted_email && <span className="mr-3">📧 {r.extracted_email}</span>}
                            {r.extracted_phone && <span>📞 {r.extracted_phone}</span>}
                          </div>
                        )}
                        <Textarea
                          placeholder="Notes..."
                          value={r.notes || ""}
                          onChange={(e) => setRows((prev) => prev.map((row) => row.id === r.id ? { ...row, notes: e.target.value } : row))}
                          onBlur={(e) => updateRow(r.id, { notes: e.target.value })}
                          className="text-xs mt-2 min-h-[40px]"
                          rows={1}
                        />
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      <div>{r.source_domain}</div>
                      <div className="mt-1 italic">"{r.search_query}"</div>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={r.status}
                        onValueChange={(v) => updateRow(r.id, { status: v })}
                      >
                        <SelectTrigger className="h-8">
                          <div className="flex items-center gap-2">
                            <span className={`h-2 w-2 rounded-full ${STATUS_COLORS[r.status] || "bg-gray-400"}`} />
                            <SelectValue />
                          </div>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="reviewed">Reviewed</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="converted">Converted</SelectItem>
                          <SelectItem value="dismissed">Dismissed</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteRow(r.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
